import {
	faChartMixedUpCircleDollar as fasChartMixedUpCircleDollar,
	faCircle as fasCircle,
	faInfoCircle as fasInfoCircle,
	faMoneyBill as fasMoneyBill,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { MeasureComponent, type MeasureComponentProps, type MeasureComponentState } from "@component/MeasureComponent";
import MortgageLeadForm from "@component/Mortgage/LeadForm/MortgageLeadForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import autobind from "autobind-decorator";
import { http, Alert, Button, FormSelect, type FormSelectOptions, List, PromisePool } from "bm-ui-kit";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import type { ReactNode } from "react";
import { Accordion, Card, Col, Collapse, Form, Row, Table } from "react-bootstrap";
import { Form as FinalForm } from "react-final-form";
import { InView } from "react-intersection-observer";
import Skeleton from "react-loading-skeleton";
import { baseDataPath, chartBaseOptions, dataPaths } from "./Barometer.data";
import type { BarometerCalculatedData, BarometerData } from "./Barometer.types";
import { LandingAccordion } from "./LandingAccordion";
import "./barometer.scss";

interface State extends MeasureComponentState {
	loadingData: boolean;
	data: BarometerData;
	amountOptions: FormSelectOptions<string, string>;
	selectedCategory: string;
	showLeadBlock: boolean;
}

type Props = MeasureComponentProps;

export class Barometer extends MeasureComponent<Props, State> {
	protected readonly productTypes: string = "MORTGAGE";
	protected readonly componentType: EventComponentType = "widget";
	private readonly promisePool = new PromisePool();
	private desktopOpen: boolean;
	protected leadBlockButtonId: string;

	public constructor(props: Props) {
		super(props);

		this.state = this.getInitialState();
	}

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			loadingData: true,
			data: {
				high: {
					history: [],
					calculated: undefined,
				},
				medium: {
					history: [],
					calculated: undefined,
				},
				low: {
					history: [],
					calculated: undefined,
				},
			},
			amountOptions: [],
			selectedCategory: "high",
			showLeadBlock: false,
		};
	}
	public componentDidMount(): void {
		this.desktopOpen = window.innerWidth > 768;

		this.loadData();
		super.componentDidMount();
	}

	private loadData(): void {
		this.setState({ loadingData: true });

		this.promisePool
			.add(
				Promise.all(
					dataPaths.map((path) =>
						http.get(baseDataPath + path.url).then((response) => {
							return {
								data: response,
								category: path.category,
								type: path.type,
							};
						}),
					),
				),
			)
			.then((allResponses) => {
				const barometerData: BarometerData = {
					high: {},
					medium: {},
					low: {},
				};

				allResponses.forEach((response) => {
					if (response.type === "history") {
						barometerData[response.category].history = response.data.sort(
							(a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
						);
					} else {
						barometerData[response.category].calculated = response.data;
					}
				});

				const amountOptions: FormSelectOptions<string, string> = [
					{
						value: "high",
						label: formatText(barometerData.high.calculated?.loanAmountRange, "ft"),
					},
					{
						value: "medium",
						label: formatText(barometerData.medium.calculated?.loanAmountRange, "ft"),
					},
					{
						value: "low",
						label: formatText(barometerData.low.calculated?.loanAmountRange, "ft"),
					},
				];

				this.setState({ loadingData: false, data: barometerData, amountOptions });
			})
			.catch((error) => {
				console.error(error);
			});
	}

	@autobind
	private handleSelect(value: string): void {
		this.setState({ selectedCategory: value });
	}

	private toggleLeadBlock(): void {
		this.setState((prevState) => ({ showLeadBlock: !prevState.showLeadBlock }));
		this.leadBlockButtonId = GoogleAnalytics4.generateId("bi");

		this.sendEvent("BM_blockToggle", {
			buttonId: this.leadBlockButtonId,
			dropdown: "Legjobb ajánlatot szeretném",
		});
	}

	@autobind
	public render(): ReactNode {
		const { wasVisible } = this.state;

		return (
			<InView
				as="div"
				{...GoogleAnalytics4.getInViewProps()}
				skip={wasVisible}
				onChange={this.onVisibilityChange}
			>
				<div className="h1 mb-3 mb-lg-4">Itt mutatjuk mennyit nyersz a Bankmonitorral</div>
				<FinalForm<{ amount: number }> initialValues={{ amount: 0 }} onSubmit={() => {}}>
					{() => (
						<Form>
							<Row>
								<Col lg={7}>
									<div className="d-lg-none">
										<Card className="shadow mb-4">{this.renderCardHeader()}</Card>
									</div>
									{this.renderChart()}
								</Col>
								<Col lg={5}>
									<Card className="shadow mb-4">
										<div className="d-none d-lg-block">{this.renderCardHeader()}</div>
										{this.renderCardBody()}
									</Card>
								</Col>
							</Row>
						</Form>
					)}
				</FinalForm>
				{this.renderAlert()}
				{this.renderLeadFormBlock()}
				<Row>
					<Col lg={7}>{this.renderThisWeek()}</Col>
					<Col lg={5}>{this.renderHowWeCalculate()}</Col>
				</Row>
			</InView>
		);
	}

	private renderChart(): ReactNode {
		const { selectedCategory, data, loadingData } = this.state;

		if (loadingData) {
			return <Skeleton count={1} height={350} />;
		}

		if (!data?.[selectedCategory]?.history || !data?.[selectedCategory]?.calculated) {
			return null;
		}

		const seriesBmData = data[selectedCategory].history.map((p) => [
			new Date(p.date).getTime(),
			p.dailyData.individualInterestRate,
		]);
		const seriesAvgData = data[selectedCategory].history.map((p) => [
			new Date(p.date).getTime(),
			p.dailyData.normalInterestRate,
		]);

		const options = {
			...chartBaseOptions,
			series: [
				{
					name: "Bankmonitor",
					color: "#fa4616",
					data: seriesBmData,
				},
				{
					name: "Banki átlag",
					color: "#72246C",
					data: seriesAvgData,
				},
			],
		};

		const {
			bestOffer: { individualInterestRate },
			averageOffer: { avgInterestRate },
		} = data[selectedCategory].calculated;

		return (
			<>
				<div className="text-md-center mb-3">
					<FontAwesomeIcon icon={fasCircle} className="text-primary" size="2xs" /> Bankmonitor ajánlata:{" "}
					<span className="fw-bold mr-3">{formatText(individualInterestRate, "%")}</span>{" "}
					<br className="d-md-none" />
					<FontAwesomeIcon icon={fasCircle} className="text-secondary" size="2xs" /> Banki átlag:{" "}
					<span className="fw-bold">{formatText(avgInterestRate, "%")}</span>
				</div>
				<HighchartsReact highcharts={Highcharts} options={options} />
			</>
		);
	}

	private renderCardHeader(): ReactNode {
		const { amountOptions, loadingData } = this.state;

		return (
			<Card.Header className="py-3 px-4">
				<div className="h4 mb-2">Add meg a hitelösszeget és hasonlítsd össze a mai ajánlatokat</div>
				{loadingData ? (
					<Skeleton count={1} height={40} />
				) : (
					<FormSelect
						name="amount"
						className="mb-0"
						options={amountOptions}
						onChange={this.handleSelect}
						prepend={<FontAwesomeIcon icon={fasMoneyBill} />}
					/>
				)}
			</Card.Header>
		);
	}

	private renderCardBody(): ReactNode {
		const { selectedCategory, data, loadingData } = this.state;

		const cellSkeleton = <Skeleton count={1} height={20} />;
		const calculatedData: BarometerCalculatedData = data[selectedCategory]?.calculated;

		const { bestOffer = undefined, averageOffer = undefined } = calculatedData || {};

		return (
			<Card.Body className="py-0 px-4">
				<Table borderless className="w-100 barometer-table">
					<thead className="bg-white">
						<tr>
							<th>Paraméter</th>
							<th>Bankmonitor ajánlat</th>
							<th>Átlagos ajánlat</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>Kamat</td>
							<td>{loadingData ? cellSkeleton : formatText(bestOffer?.individualInterestRate, "%")}</td>
							<td>{loadingData ? cellSkeleton : formatText(averageOffer?.avgInterestRate, "%")}</td>
						</tr>
						<tr>
							<td>Havi törlesztő</td>
							<td>{loadingData ? cellSkeleton : formatText(bestOffer?.monthlyInstallment, "ft")}</td>
							<td>{loadingData ? cellSkeleton : formatText(averageOffer?.monthlyInstallment, "ft")}</td>
						</tr>
						<tr>
							<td>Futamidő</td>
							<td>{loadingData ? cellSkeleton : `${bestOffer?.loanDuration} év`}</td>
							<td>{loadingData ? cellSkeleton : `${averageOffer?.loanDuration} év`}</td>
						</tr>
						<tr>
							<td>Teljes visszafizetés</td>
							<td>
								{loadingData
									? cellSkeleton
									: `${(bestOffer?.totalRepayment / 1_000_000).toFixed(1)} mFt`}
							</td>
							<td>
								{loadingData
									? cellSkeleton
									: `${(averageOffer?.totalRepayment / 1_000_000).toFixed(1)} mFt`}
							</td>
						</tr>
					</tbody>
				</Table>
			</Card.Body>
		);
	}

	private renderLeadFormBlock(): ReactNode {
		const { showLeadBlock } = this.state;

		return (
			<Collapse in={showLeadBlock} className="mb-4">
				<Row className="justify-content-center align-items-center flex-column-reverse flex-lg-row p-3">
					<Col className="text-block" lg={7}>
						<div className="h3  font-weight-bold mb-4">
							Hogyan kaphatok kedvezményt a megjelenített kamatszintekből?
						</div>
						<List itemStyle="tick" variant="gold">
							<List.Item>
								A <strong>Bankmonitor</strong> Magyarország{" "}
								<strong>legnagyobb pénzügyi közvetítő</strong> cége*, így sok esetben még a{" "}
								<strong>legalacsonyabb</strong> meghirdetett{" "}
								<strong>kamatszintekre is további kedvezményt</strong> kapunk ügyfeleinknek a banktól.
							</List.Item>
							<List.Item>
								<strong>Szakértőnik</strong> az egész országban azon dolgoznak, hogy minden hiteligényre{" "}
								<strong>a lehető legkedvezőbb feltételekkel</strong> (kamat és egyéb költségek)
								rendelkező hitelt biztosítsák.
							</List.Item>
							<List.Item>
								<strong>Az egyedi kamatkedvezmény</strong> minden esetben az{" "}
								<strong>igazolt jövedelem</strong> és egyéb kiemelt jellemnzők{" "}
								<strong>függvényében</strong> biztosítható.
							</List.Item>
							<List.Item>
								<strong>Szakértőink szolgáltatása ingyenes</strong> (törvényi előírások alapján
								kizárólag a bank fizethet kompenzációt a bankfiókok helyett elvégzett munka
								ellenértékeként).
							</List.Item>
							<List.Item>
								Minden
								<strong>
									{" "}
									10 millió forint felvett hitel esetén 1 százalék kamatkedvezmény közel 1,5 millió Ft
									megtakarítást
								</strong>{" "}
								jelent a teljes visszafizetés vonatkozásában.
							</List.Item>
							<List.Item>
								Hitelfelvételednél kiemelten fontos, hogy az{" "}
								<strong>összes elérhető támogatást és támogatott hitelt is igénybe vedd</strong>: ebben
								is segítünk!
							</List.Item>
						</List>
						<small>
							*A 2022-es évre közzétett nettó árbevételek alapján a pénzpiaci közvetítők között.
						</small>
					</Col>
					<Col className="pb-4 pb-lg-0" lg={5} md={8}>
						<MortgageLeadForm
							title="Kérj segítséget szakértőnktől!"
							ga4Event={{
								buttonId: this.leadBlockButtonId,
								componentName: "MortgageBarometerLeadForm",
							}}
							crossSellOption
							withLoanAmount
							fluid
						/>
					</Col>
				</Row>
			</Collapse>
		);
	}

	private renderAlert(): ReactNode {
		const { data, selectedCategory, loadingData } = this.state;
		if (loadingData) {
			return <Skeleton count={1} height={50} />;
		}

		const calculatedData = data[selectedCategory].calculated;

		const {
			description: { lowerThanAverage, loanAmount, loanDuration, annualSavings },
		} = calculatedData;

		// NOTE: non-standard alert background color added in css
		return (
			<Alert
				variant="primary"
				icon={<FontAwesomeIcon icon={fasChartMixedUpCircleDollar} className="text-primary" />}
				size="lg"
				alignItems="start"
				className="mb-3 mb-lg-4 fs-md fw-normal text-dark barometer-alert"
			>
				Jelenleg{" "}
				<strong>
					a Bankmonitor legjobb ajánlata {lowerThanAverage.toFixed(2)} százalékponttal alacsonyabb a magyar
					banki átlagnál.
				</strong>{" "}
				{formatText(loanAmount, "000")} forint hitelösszegnél {loanDuration} éves futamidő mellett{" "}
				<strong>éves szinten {formatText(annualSavings, "000")} forintot spórolsz meg</strong>.
				<br />
				<Button className="mt-3 fw-bold" variant="primary" onClick={() => this.toggleLeadBlock()}>
					Legjobb ajánlatot szeretném
				</Button>
			</Alert>
		);
	}

	private renderThisWeek(): ReactNode {
		const { selectedCategory, data, loadingData } = this.state;
		if (loadingData) {
			return <Skeleton count={1} height={200} />;
		}

		const calculatedData: BarometerCalculatedData = data[selectedCategory].calculated;
		const { bestOffer, averageOffer, description } = calculatedData;

		return (
			<>
				<div className="h3">Így alakult a lakáshitelek kamata a héten</div>
				<p className="mb-3 fw-normal">
					Jelenleg a legalább 10 évig fix kamatozású lakáshitelek átlagos kamata{" "}
					{formatText(averageOffer.avgInterestRate, "%")}. Egy hét alatt{" "}
					{description.percentagePointChange.toFixed(2)} százalékpontos változás történt. A Bankmonitornál
					jelenleg elérhető legkedvezőbb ajánlat kamata {formatText(bestOffer.individualInterestRate, "%")},
					ez {description.weeklyPercentagePointDifference.toFixed(2)} százlékpont változás az egy héttel
					korábbi értékhez képest. {formatText(description.loanAmount, "ft")} hitelösszegnél{" "}
					{description.loanDuration} éves futamidő mellett havi szinten{" "}
					{formatText(description.monthlySaving, "ft")}-ot, összességében{" "}
					{formatText(description.totalSaving, "ft")}-ot lehet spórolni azzal, ha a Bankmonitor legkedvezőbb
					ajánlatát választod.
				</p>
			</>
		);
	}

	private renderHowWeCalculate(): ReactNode {
		const { selectedCategory, data, loadingData } = this.state;
		if (loadingData) {
			return <Skeleton count={1} height={200} />;
		}

		const calculatedData: BarometerCalculatedData = data[selectedCategory].calculated;
		const { description } = calculatedData;

		return (
			<Accordion defaultActiveKey={this.desktopOpen ? "howWeCounted" : undefined}>
				<LandingAccordion
					icon={<FontAwesomeIcon icon={fasInfoCircle} className="text-primary" />}
					title="Így számoltunk"
					key="howWeCounted"
					id="howWeCounted"
					className="bg-white border"
					showIconOnAllDevices
				>
					<hr className="mt-0" />
					<p className="text-muted pl-4 pl-md-0">
						A legkedvezőbb kamat meghatározásakor a Bankmonitornál az elmúlt 7 nap érdeklődők által kapott
						legkedvezőbb ajánlatot vettük alapul ({formatText(description.bestOfferLowerLimit, "000")} és{" "}
						{formatText(description.bestOfferHigherLimit, "ft")} közötti hitelösszeg esetében). Az átlagos
						kamat a bankok ajánlatainak átlagkamatát takarja {formatText(description.loanAmount, "ft")}{" "}
						kölcsönösszeg, {description.loanDuration} éves futamidő, {formatText(description.income, "ft")}{" "}
						jövedelem és legalább 10 évig fix kamatozás mellett.
					</p>
				</LandingAccordion>
			</Accordion>
		);
	}

	protected getComponentName(): string {
		return "Barometer";
	}
}
